import React from 'react';
import { PropTypes } from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { DateUtil, MESSAGE_TYPE } from 'doctivity-shared/utils';
import {
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { SmartMarkdownLink, isSafeHref } from './SmartMarkdownLink';

/**
 * Rendering for a single notification: any access notices, the title, who sent it
 * and when, then the Markdown body.
 *
 * There is deliberately no call-to-action button. A notification often covers two
 * or three things, so one generic button at the bottom can't say where it goes —
 * and admins can already write links into the body as Markdown, in the sentence
 * that explains them. `Messages.link_url` is still stored (it records which entity
 * a system event is about) but nothing renders it.
 */

/**
 * Where clicking a notification should actually take you.
 *
 * A system_event exists only to say "a note / a mileage log was assigned to you".
 * Routing those to a page *about* the note, which then offers a link to the note,
 * is a hop that earns nothing — so they go straight to the thing itself. An
 * announcement has real content of its own, so it opens its own page.
 *
 * Shared by the bell and the inbox table so the two can't drift into disagreeing
 * about where a row goes.
 */
export const notificationTarget = (n) => {
  if (!n) return '/notifications';
  if (Number(n.type) === MESSAGE_TYPE.SYSTEM_EVENT && isSafeHref(n.link_url || '')) {
    return n.link_url;
  }
  return n.permalink || `/notifications/${n.id}`;
};

/**
 * Does this notification have a page of its own worth showing?
 *
 * False for a note/mileage assignment, which is just a pointer at someone's own
 * work item: its permalink is reachable but empty of value. NotificationPage uses
 * this to pass straight through to the note instead of rendering a page about it.
 * Announcements have real content, so they get their own page.
 */
export const hasOwnPage = (n) => (
  !!n && notificationTarget(n) === (n.permalink || `/notifications/${n.id}`)
);

const NotificationView = ({ notification, showMeta = true }) => {
  if (!notification) return null;

  return (
    <>
      {notification.viewing_as_admin && (
        <Alert severity='info' sx={{ mb: 2 }}>
          Viewing as admin — this notification was not sent to you, so opening it does not
          change anyone&apos;s read status.
        </Alert>
      )}
      {notification.system_wide_access && (
        <Alert severity='info' sx={{ mb: 2 }}>
          This is a Doctivity-wide announcement. It wasn&apos;t delivered to your
          notifications list — it was sent before you joined, or shared with you — so it
          won&apos;t appear under the bell.
        </Alert>
      )}

      <Typography variant='h6' gutterBottom>{notification.title}</Typography>

      {showMeta && (
        <Typography variant='body2' color='textSecondary' sx={{ mb: 2 }}>
          {notification.sender_name || 'Doctivity'}
          {notification.created_at ? ` · ${DateUtil.formatFromDB(notification.created_at)}` : ''}
        </Typography>
      )}

      {/* Wrap so the Markdown body uses the app's (theme) font, not the browser default. */}
      <Box sx={{ typography: 'body1' }}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: SmartMarkdownLink }}>
          {notification.body_markdown || ''}
        </ReactMarkdown>
      </Box>
    </>
  );
};

NotificationView.propTypes = {
  notification: PropTypes.object,
  showMeta: PropTypes.bool,
};

export { NotificationView };
export default NotificationView;
